/**
 * TradingPanel - Container component that composes trading UI.
 * Combines OrderForm, OpenOrders, and PositionSummary.
 */

import { memo } from 'react'
import { cn } from '../../lib/utils'
import { OrderForm } from './OrderForm'
import { OpenOrders } from './OpenOrders'
import { PositionSummary } from './PositionSummary'
import { getAvailableYesShares } from '../../engine/positions'
import type { Market, Position, Order, NewOrder } from '../../engine/types'

export interface EnabledFeatures {
  /** Allow limit orders */
  limitOrders: boolean
  /** Allow market orders */
  marketOrders: boolean
  /** Allow canceling orders */
  cancelOrders: boolean
}

export interface TradingPanelProps {
  /** The current market */
  market: Market | null
  /** User's available balance */
  userBalance: number
  /** User's current positions */
  userPositions: Position[]
  /** User's open orders */
  openOrders: Order[]
  /** Current market price for P&L calculations */
  currentPrice: number | null
  /** Callback to place an order */
  onPlaceOrder: (order: NewOrder) => void
  /** Callback to cancel an order */
  onCancelOrder: (orderId: string) => void
  /** Which features are enabled for this lesson/mode */
  enabledFeatures: EnabledFeatures
  /** Pre-fill price in order form */
  selectedPrice?: number
  /** Optional className */
  className?: string
}

/**
 * TradingPanel composes the trading interface components.
 */
export const TradingPanel = memo(function TradingPanel({
  market,
  userBalance,
  userPositions,
  openOrders,
  currentPrice,
  onPlaceOrder,
  onCancelOrder,
  enabledFeatures,
  selectedPrice,
  className,
}: TradingPanelProps) {
  // Check if any order types are enabled
  const canTrade = enabledFeatures.limitOrders || enabledFeatures.marketOrders
  const availableYesShares = market
    ? getAvailableYesShares(userPositions, market.id, openOrders)
    : 0

  if (!market) {
    return (
      <div
        className={cn(
          'rounded-lg border border-border bg-card p-6 text-center',
          className
        )}
      >
        <p className="text-sm text-muted-foreground">
          No market selected. Start a lesson to begin trading.
        </p>
      </div>
    )
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Balance display */}
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Your Balance</span>
          <span className="text-xl font-bold tabular-nums text-foreground">
            ${userBalance.toFixed(2)}
          </span>
        </div>
      </div>

      {/* Order Form */}
      {canTrade && (
        <OrderForm
          userBalance={userBalance}
          enableLimitOrders={enabledFeatures.limitOrders}
          enableMarketOrders={enabledFeatures.marketOrders}
          onPlaceOrder={onPlaceOrder}
          selectedPrice={selectedPrice}
          availableYesShares={availableYesShares}
        />
      )}

      {/* Positions */}
      <PositionSummary
        positions={userPositions}
        currentPrice={currentPrice}
      />

      {/* Open Orders */}
      {(enabledFeatures.limitOrders || openOrders.length > 0) && (
        <OpenOrders
          orders={openOrders}
          onCancelOrder={onCancelOrder}
          enableCancel={enabledFeatures.cancelOrders}
        />
      )}

      {/* Trading disabled message */}
      {!canTrade && (
        <div className="rounded-lg border border-border bg-muted/50 p-4 text-center">
          <p className="text-sm text-muted-foreground">
            Trading is not available in this lesson. Watch the market to learn
            how it works.
          </p>
        </div>
      )}
    </div>
  )
})
